import { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { useTourStore } from '../store/tour-store';
import { useTourTarget } from '../hooks/use-tour-target';
import { registroDeTours, resolveTourConfig } from '../data/tour-registry';
import { useHayDialogoAbierto } from '../hooks/use-hay-dialogo-abierto';
import { esEmpresaDemo } from '@/features/mini-prixia/empresa-demo';
import { useHayMomentoAbierto } from '@/features/mini-prixia/momento-abierto';
import { TourOverlay } from './tour-overlay';
import { TourTooltip } from './tour-tooltip';
import { TourFab } from './tour-fab';
import type { TourConfig, TourStep } from '../types/tour.types';

export function TourProvider() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isActive,
    currentTourId,
    currentStepIndex,
    selectedLevel,
    completedTours,
    nextStep,
    prevStep,
    endTour,
    markCompleted,
  } = useTourStore();

  const [tourConfig, setTourConfig] = useState<TourConfig | null>(null);
  const [loadedConfigs, setLoadedConfigs] = useState<Record<string, TourConfig>>({});

  // Mini Prixia (X3): una empresa demo suma los recorridos de sus misiones.
  const esDemo = esEmpresaDemo();
  const registro = useMemo(() => registroDeTours(esDemo), [esDemo]);

  // Resolve current route to tour config
  const currentRoute = location.pathname;

  // Find matching route key (handle param routes)
  const pestana = new URLSearchParams(location.search).get('tab');
  const matchedRouteKey = useMemo(() => {
    // Una pestaña con tour propio (Productos › Vincular) le gana a su pantalla.
    if (pestana && registro[`${currentRoute}?tab=${pestana}`])
      return `${currentRoute}?tab=${pestana}`;
    // Productos › Catálogo de las tiendas no tiene recorrido propio: no hereda el de «Mis productos».
    if (esDemo && currentRoute === '/compite/productos' && pestana === 'catalogo') return null;
    // Exact match first
    if (registro[currentRoute]) return currentRoute;

    // Prefix match for parameterized routes (e.g. /pricing-models/abc → /pricing-models/)
    // Sort by key length descending so longer prefixes match first
    const prefixKeys = Object.keys(registro)
      .filter((key) => key.endsWith('/'))
      .sort((a, b) => b.length - a.length);
    for (const prefix of prefixKeys) {
      if (currentRoute.startsWith(prefix) && currentRoute.length > prefix.length) {
        return prefix;
      }
    }

    // Fallback: try base path (pop last segment)
    const segments = currentRoute.split('/').filter(Boolean);
    while (segments.length > 0) {
      const path = '/' + segments.join('/');
      if (registro[path]) return path;
      segments.pop();
    }
    return null;
  }, [currentRoute, pestana, registro, esDemo]);

  // Load tour config when route changes
  useEffect(() => {
    if (!matchedRouteKey) {
      setTourConfig(null);
      return;
    }

    // Use cached config if available (la caché va por registro: la misma ruta puede
    // tener otro recorrido en una empresa demo).
    const clave = `${esDemo ? 'demo' : 'comun'}:${matchedRouteKey}`;
    if (loadedConfigs[clave]) {
      setTourConfig(loadedConfigs[clave]);
      return;
    }

    let vigente = true;
    resolveTourConfig(matchedRouteKey, registro).then((config) => {
      // Si mientras cargaba se cambió de pantalla, esta respuesta ya no manda.
      if (!vigente) return;
      if (config) {
        setLoadedConfigs((prev) => ({ ...prev, [clave]: config }));
        setTourConfig(config);
      } else {
        setTourConfig(null);
      }
    });
    return () => {
      vigente = false;
    };
  }, [matchedRouteKey, registro, esDemo]);

  // Filter steps by selected level
  const filteredSteps = useMemo((): TourStep[] => {
    if (!tourConfig) return [];
    return tourConfig.steps.filter((step) => {
      if (selectedLevel === 'avanzado') return true;
      if (selectedLevel === 'intermedio')
        return step.levels.includes('basico') || step.levels.includes('intermedio');
      return step.levels.includes('basico');
    });
  }, [tourConfig, selectedLevel]);

  // Current step
  const currentStep = filteredSteps[currentStepIndex] ?? null;

  // Target rect for current step
  const targetRect = useTourTarget(isActive && currentStep ? currentStep.target : null);

  /*
    Mini Prixia (X3), sólo en la demo: la tarjeta de misiones navega y arranca el
    recorrido en el mismo momento. Se muestra recién cuando la pantalla cargó SU
    recorrido (mismo id) y el objetivo del paso (o pasaron 3 s); si en 4 s la
    pantalla no es la suya, se cierra solo. Con un diálogo abierto, se pausa.
    Las empresas comunes siguen exactamente como antes.
  */
  const hayDialogo = useHayDialogoAbierto(esDemo && isActive);
  const esElDeLaPantalla = tourConfig !== null && tourConfig.id === currentTourId;
  const [esperaVencida, setEsperaVencida] = useState(false);
  useEffect(() => {
    setEsperaVencida(false);
    if (!esDemo || !isActive) return;
    const t = window.setTimeout(() => setEsperaVencida(true), 3000);
    return () => window.clearTimeout(t);
  }, [esDemo, isActive, currentTourId, currentStepIndex]);
  useEffect(() => {
    if (!esDemo || !isActive || esElDeLaPantalla) return;
    const t = window.setTimeout(() => endTour(), 4000);
    return () => window.clearTimeout(t);
  }, [esDemo, isActive, esElDeLaPantalla, endTour]);
  // Con un momento de la mini en pantalla (animación 3, 5…) ningún recorrido se abre encima.
  const hayMomento = useHayMomentoAbierto();
  const aLaVista =
    !hayMomento &&
    (!esDemo || (esElDeLaPantalla && !hayDialogo && (targetRect !== null || esperaVencida)));
  // X3 (decisión 22): el último paso de una misión pide HACER algo en la pantalla.
  const esAccion = !!currentStep?.accion;
  // Ronda Y: el paso de cierre (`trasCumplir`) también deja usar la pantalla, y no
  // cuenta en el «N de M» hasta que se llega a él.
  const dejaUsar = esAccion || !!currentStep?.trasCumplir;
  const pasosAContar = currentStep?.trasCumplir
    ? filteredSteps.length
    : filteredSteps.filter((s) => !s.trasCumplir).length;
  // Ronda Y (sólo demo): el botón del paso se ve mientras su condición está en pantalla.
  const botonDelPaso = esDemo && isActive ? (currentStep?.boton ?? null) : null;
  const condicionDelBoton = useTourTarget(botonDelPaso ? botonDelPaso.siEsta : null);
  const irConElBoton = useCallback(() => {
    if (!botonDelPaso) return;
    endTour();
    navigate(botonDelPaso.ruta);
  }, [botonDelPaso, endTour, navigate]);

  // Scroll target into view
  useEffect(() => {
    if (!isActive || !currentStep) return;
    const el = document.querySelector(`[data-tour="${currentStep.target}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isActive, currentStep]);

  const handleNext = useCallback(() => {
    if (currentStepIndex >= filteredSteps.length - 1) {
      // Tour completed
      if (currentTourId) markCompleted(currentTourId);
    } else {
      nextStep();
    }
  }, [currentStepIndex, filteredSteps.length, currentTourId, markCompleted, nextStep]);

  const handleClose = useCallback(() => {
    endTour();
  }, [endTour]);

  // Count pending tours
  const pendingCount = useMemo(() => {
    const totalRoutes = Object.keys(registro).length;
    return totalRoutes - completedTours.length;
  }, [completedTours, registro]);

  return (
    <>
      {/* Overlay + Tooltip when tour is active */}
      <AnimatePresence>
        {isActive && currentStep && aLaVista && (
          <>
            {/* Un paso de acción sin su objetivo a la vista (p. ej. ya en Revisar precios) no oscurece nada. */}
            {!(dejaUsar && targetRect === null) && (
              <TourOverlay targetRect={targetRect} onClose={handleClose} dejaPasar={dejaUsar} />
            )}
            <TourTooltip
              step={currentStep}
              stepIndex={currentStepIndex}
              totalSteps={pasosAContar}
              targetRect={targetRect}
              onNext={handleNext}
              onPrev={prevStep}
              onClose={handleClose}
              isLast={currentStepIndex >= filteredSteps.length - 1}
              esAccion={esAccion}
              sinNivel={esDemo}
              boton={
                botonDelPaso && condicionDelBoton
                  ? { texto: botonDelPaso.texto, onIr: irConElBoton }
                  : undefined
              }
            />
          </>
        )}
      </AnimatePresence>

      {/* FAB - always visible when not in tour */}
      <TourFab tourConfig={tourConfig} pendingCount={Math.max(0, pendingCount)} />
    </>
  );
}
